/**
 * Product Tabs - Accessible tab functionality for product descriptions
 * Vanilla JavaScript implementation with ARIA support
 */
class ProductTabs {
  constructor() {
    this.tabs = new Map();
    this.init();
  }

  init() {
    // Find all tab containers on the page
    const tabContainers = document.querySelectorAll('[data-product-id][role="tablist"]');
    
    tabContainers.forEach(container => {
      const productId = container.getAttribute('data-product-id');
      
      // Skip if already initialized
      if (this.tabs.has(productId)) {
        return;
      }
      
      this.initializeTabs(productId, container);
    });
  }

  initializeTabs(productId, tabList) {
    // Get all tab buttons and scroll links
    const allTabItems = Array.from(tabList.querySelectorAll('[role="tab"], .product-tabs__button--link'));
    const tabs = allTabItems.filter(item => item.getAttribute('role') === 'tab');
    const panels = document.querySelectorAll(`[data-product-id="${productId}"][role="tabpanel"]`);
    
    if (tabs.length === 0 || panels.length === 0) {
      return;
    }

    // Activate first tab by default
    this.activateTab(productId, tabs[0], 0);

    // Add click handlers for all items (tabs and scroll links)
    allTabItems.forEach((item, index) => {
      // Check if this is a scroll link (Reviews button)
      const scrollTarget = item.getAttribute('data-scroll-to');
      if (scrollTarget) {
        item.addEventListener('click', (e) => {
          e.preventDefault();
          const targetElement = document.querySelector(scrollTarget);
          if (targetElement) {
            targetElement.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start' 
            });
            // Update URL hash without scrolling
            if (history.pushState) {
              history.pushState(null, null, scrollTarget);
            }
          }
        });
      } else if (item.getAttribute('role') === 'tab') {
        // Regular tab button - find its index in tabs array
        const tabIndex = tabs.indexOf(item);
        if (tabIndex !== -1) {
          item.addEventListener('click', (e) => {
            e.preventDefault();
            this.handleTabClick(productId, item, tabIndex);
          });

          // Keyboard navigation for tabs only
          item.addEventListener('keydown', (e) => {
            this.handleKeyDown(productId, e, tabs, tabIndex);
          });
        }
      }
    });

    // Store reference
    this.tabs.set(productId, { tabs, panels });
  }

  handleTabClick(productId, clickedTab, index) {
    this.activateTab(productId, clickedTab, index);
    
    // Dispatch custom event for other scripts
    const event = new CustomEvent('productTabChange', {
      detail: {
        productId,
        tabIndex: index,
        tabId: clickedTab.id,
        panelId: clickedTab.getAttribute('aria-controls')
      }
    });
    document.dispatchEvent(event);
  }

  activateTab(productId, tab, index) {
    const { tabs, panels } = this.tabs.get(productId) || {};
    
    if (!tabs || !panels) {
      // Fallback: find panels dynamically
      const allTabs = document.querySelectorAll(`[data-product-id="${productId}"][role="tab"]`);
      const allPanels = document.querySelectorAll(`[data-product-id="${productId}"][role="tabpanel"]`);
      
      if (allTabs.length === 0 || allPanels.length === 0) {
        return;
      }
      
      // Update stored reference
      this.tabs.set(productId, { tabs: Array.from(allTabs), panels: Array.from(allPanels) });
      return this.activateTab(productId, tab, index);
    }

    // Deactivate all tabs and panels
    tabs.forEach((t, i) => {
      const isActive = i === index;
      t.setAttribute('aria-selected', isActive);
      t.setAttribute('tabindex', isActive ? '0' : '-1');
      t.classList.toggle('product-tabs__button--active', isActive);
      
      if (panels[i]) {
        if (isActive) {
          panels[i].removeAttribute('hidden');
          panels[i].classList.add('product-tabs__panel--active');
        } else {
          panels[i].setAttribute('hidden', '');
          panels[i].classList.remove('product-tabs__panel--active');
        }
      }
    });
  }

  handleKeyDown(productId, event, tabs, currentIndex) {
    let targetIndex = currentIndex;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        targetIndex = (currentIndex + 1) % tabs.length;
        break;
      
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        targetIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        break;
      
      case 'Home':
        event.preventDefault();
        targetIndex = 0;
        break;
      
      case 'End':
        event.preventDefault();
        targetIndex = tabs.length - 1;
        break;
      
      default:
        return;
    }

    tabs[targetIndex].focus();
    this.activateTab(productId, tabs[targetIndex], targetIndex);
  }

  // Public method to programmatically switch tabs
  switchTab(productId, tabIndex) {
    const { tabs } = this.tabs.get(productId) || {};
    if (tabs && tabs[tabIndex]) {
      this.activateTab(productId, tabs[tabIndex], tabIndex);
      tabs[tabIndex].focus();
    }
  }

  // Reinitialize tabs (useful for dynamic content)
  reinit() {
    this.tabs.clear();
    this.init();
  }
}

// Initialize singleton instance
window.ProductTabs = new ProductTabs();

// Reinitialize on dynamic content updates (e.g., AJAX)
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.ProductTabs.init();
  });
} else {
  window.ProductTabs.init();
}

// Shopify Theme Editor support
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    // Reinitialize tabs when section is loaded in theme editor
    const section = event.target;
    if (section.querySelector('[data-product-id][role="tablist"]')) {
      window.ProductTabs.reinit();
    }
  });

  document.addEventListener('shopify:section:reorder', () => {
    window.ProductTabs.reinit();
  });
}

// Reinitialize when new tabs are added to the DOM
if (typeof MutationObserver !== 'undefined') {
  const observer = new MutationObserver((mutations) => {
    let shouldReinit = false;
    mutations.forEach((mutation) => {
      if (mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1 && (
            node.matches && node.matches('[data-product-id][role="tablist"]') ||
            node.querySelector && node.querySelector('[data-product-id][role="tablist"]')
          )) {
            shouldReinit = true;
          }
        });
      }
    });
    if (shouldReinit) {
      window.ProductTabs.init();
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
}
